import type {
  Me,
  SeatSelection,
  EventCarriagesPayload,
  CarriageInput,
  CarriageTicketInput,
  CarriageTicketOptions,
  EventListItem,
  EventType,
  Department,
  EventUpdate,
  EventCreate,
  UserOption,
  Activity,
  ActivityCreate,
  ActivityUpdate,
  Component,
  ComponentCreate,
  ComponentUpdate,
  LibraryComponent,
  LibraryComponentCreate,
  LibraryComponentPatch,
  CopyFromLibraryResult,
  LibraryActivity,
  LibraryActivityCreate,
  LibraryActivityPatch,
  CopyActivitiesFromLibraryResult,
  Service,
  ServiceCreate,
  ServiceUpdate,
  TimetablePayload,
  RosterResponse,
  RosterAssignment,
  RosterAssignmentCreate,
  RosterAssignmentPatch,
  VolunteerOffer,
  VolunteerCall,
  VolunteerCallSetBody,
  LibraryRole,
  LibraryRoleCreate,
  LibraryRolePatch,
  EventRoleSummary,
  EventRolesSetBody,
  RosterAvailability,
  RosterAvailabilityUpsertBody,
  RosterAvailabilityUpsertResult,
  DashboardPayload,
  WeatherPayload,
  ChecklistTemplateSummary,
  ChecklistTemplate,
  ChecklistTemplateBody,
  EventChecklistResponse,
  EventChecklistItemAdd,
  EventChecklistItemPatch,
  HazardLibraryEntry,
  HazardLibraryCreate,
  HazardLibraryPatch,
  EventRisk,
  EventRiskBody,
  EventNote,
  NoteCreateBody,
  NotePatchBody,
  BudgetCategory,
  BudgetPayload,
  BudgetLineBody,
  MeracRollupPayload,
  MeracSummaryMap,
  FinancialsPayload,
  EventIncomePayload,
  EventAction,
  EventActionCreate,
  EventActionPatch,
  PlanTask,
  PlanTaskCreate,
  PlanTaskPatch,
  StationMarker,
  StationMarkerCreate,
  StationMarkerPatch,
  StationSlug,
  BriefingTopic,
  BriefingTopicBody,
  EventBriefingsPayload,
  CommsSendResult,
  ReportsFilters,
  ReportsPayload,
  VolunteerShiftsPayload,
  VolunteerProfile,
  VolunteerProfileBody,
  MyActionsPayload,
  AdminUser,
  AdminUserDetail,
  AdminUserPatch,
  ArchivedEventListItem,
  ArchiveEventResult,
  ArchivePreview,
  BuildChecklistPayload,
  CreateUserFromEntraResult,
  MeracEventCode,
  Person,
  ServiceCrew,
  AssignCrewBody,
  OperatingDay,
  UpsertOperatingDayBody,
  ImportWttBody,
  ImportWttResult,
  NameOption,
  EventResource,
  EventResourceAttach,
  QuickReferencePayload,
  LivePulsePayload,
  LiveShiftRecord,
  LiveFlagRecord,
  LiveFlagCreateBody,
  EventWalkthroughState,
  BroadcastRecord,
  BroadcastCreateBody,
  ResourcesDirectoryPayload,
  TicketType,
  TicketTypeCreate,
  TicketTypePatch,
  EventTicketType,
  EventTicketTypeCreate,
  EventTicketTypePatch,
  TicketTypeMeracMapping,
  TicketMeracCandidate,
  EventServiceInfo,
  EventCatering,
  EventCateringBody,
  CateringReportPayload,
  ServiceTimetableReportPayload,
  PresentationTimetablePayload,
  BriefingTicket,
  FaqSuggestion,
  EventFlag,
  EventBriefingDoc,
  ForecastPayload,
  ForecastCommitResult,
  ForecastTeamRow,
  ForecastPhase,
  LibraryFunction,
  LibraryFunctionBody,
  ForecastFinancePayload,
  ResearchChatResponse,
  ResearchRunResponse,
  ProgrammeData,
  ProgrammeCategory,
  ProgrammeBlock,
  ProgrammeBlockCreate,
  ProgrammeBlockPatch,
  ProgrammeCategoryCreate,
  ProgrammeCategoryPatch,
  ProgrammeExport
} from './types'

// Tiny fetch wrapper. Throws Error('${status} ${statusText}: ${body}') on
// non-2xx so consumers can pattern-match `error.message.startsWith('404')`
// (kept tightly coupled - decision logged in project.md). Returns undefined
// for 204 No Content responses; T must be `void` (or compatible) at the
// call site for those endpoints.
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}: ${text}`)
  }
  // 204 No Content has no body - calling .json() on it would throw a
  // SyntaxError. Return undefined as T for callers (e.g. DELETE) that
  // type T as void.
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const ApiClient = {
  me:           () => api<Me>('/api/me'),
  diag:         () => api<Record<string, unknown>>('/api/diag'),

  // Programming Overview — Calendar tab 1 (spec 41).
  programme: {
    get:  (year: number) => api<ProgrammeData>(`/api/programme?year=${year}`),
    categories: () => api<ProgrammeCategory[]>('/api/programme/categories'),
    createBlocks: (body: ProgrammeBlockCreate) =>
      api<ProgrammeBlock[]>('/api/programme/blocks', { method: 'POST', body: JSON.stringify(body) }),
    updateBlock: (id: number, body: ProgrammeBlockPatch) =>
      api<ProgrammeBlock>(`/api/programme/blocks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteBlock: (id: number) =>
      api<void>(`/api/programme/blocks/${id}`, { method: 'DELETE' }),
    updateGroup: (body: { year: number; title: string; newTitle?: string; categoryId?: number }) =>
      api<ProgrammeData>('/api/programme/blocks/update-group', { method: 'POST', body: JSON.stringify(body) }),
    deleteGroup: (body: { year: number; title: string }) =>
      api<ProgrammeData>('/api/programme/blocks/delete-group', { method: 'POST', body: JSON.stringify(body) }),
    createCategory: (body: ProgrammeCategoryCreate) =>
      api<ProgrammeCategory>('/api/programme/categories', { method: 'POST', body: JSON.stringify(body) }),
    updateCategory: (id: number, body: ProgrammeCategoryPatch) =>
      api<ProgrammeCategory>(`/api/programme/categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteCategory: (id: number) =>
      api<void>(`/api/programme/categories/${id}`, { method: 'DELETE' }),
    updateYear: (year: number, body: { title?: string; subtitle?: string }) =>
      api<ProgrammeData>(`/api/programme/year/${year}`, { method: 'PATCH', body: JSON.stringify(body) }),
    export: (year: number) => api<ProgrammeExport>(`/api/programme/export?year=${year}`),
    import: (year: number, body: unknown) =>
      api<ProgrammeData>(`/api/programme/import?year=${year}`, { method: 'POST', body: JSON.stringify(body) })
  },
  eventTypes:   () => api<EventType[]>('/api/event-types'),
  departments:  () => api<Department[]>('/api/departments'),
  events:       () => api<EventListItem[]>('/api/events'),
  event:        (id: number) => api<EventListItem>(`/api/events/${id}`),
  createEvent:  (body: EventCreate) =>
    api<EventListItem>('/api/events', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateEvent:  (id: number, body: EventUpdate) =>
    api<EventListItem>(`/api/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  // The Ticketing tab's Carriages card: the formation (carriage instances with
  // their ticket assignments) and the "Seats" choice.
  eventCarriages: (id: number) => api<EventCarriagesPayload>(`/api/events/${id}/carriages`),
  // PUT the formation (send back each carriage's id to keep it and its
  // assignments; a bare code is a new carriage) and/or the Seats choice.
  updateEventCarriages: (id: number, body: { carriages?: CarriageInput[]; seatSelection?: SeatSelection | null }) =>
    api<EventCarriagesPayload>(`/api/events/${id}/carriages`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  // Replace one carriage's ticket assignments: which ticket-build types and/or
  // MERAC ticket classes sit on it (exactly one id each) and how many seats each.
  updateCarriageTickets: (id: number, carriageId: number, tickets: CarriageTicketInput[]) =>
    api<EventCarriagesPayload>(`/api/events/${id}/carriages/${carriageId}/tickets`, {
      method: 'PUT',
      body: JSON.stringify({ tickets })
    }),
  // What the Assign dialog may offer: the MERAC ticket classes already selling
  // on the event (from the synced sales) and the ticket build's types.
  carriageTicketOptions: (id: number) => api<CarriageTicketOptions>(`/api/events/${id}/carriages/ticket-options`),
  // Merge same-named duplicate events into one multi-date event (item 8).
  mergeEvents:  (primaryId: number, mergeIds: number[]) =>
    api<{ primaryId: number; merged: number }>('/api/events/merge', {
      method: 'POST',
      body: JSON.stringify({ primaryId, mergeIds })
    }),
  users:        () => api<UserOption[]>('/api/users'),
  people:       () => api<Person[]>('/api/people'),

  // Tweak T2a — Event archive surface.
  archivedEvents: () => api<ArchivedEventListItem[]>('/api/events/archived'),
  archivePreview: (id: number) =>
    api<ArchivePreview>(`/api/events/${id}/archive/preview`),
  archiveEvent:   (id: number) =>
    api<ArchiveEventResult>(`/api/events/${id}/archive`, { method: 'POST' }),
  unarchiveEvent: (id: number) =>
    api<{ archived: false }>(`/api/events/${id}/unarchive`, { method: 'POST' }),

  // Tweak T4 — server-derived build checklist (replaces the cosmetic
  // event.progress >= step.threshold logic in OverviewTab).
  buildChecklist: (eventId: number) =>
    api<BuildChecklistPayload>(`/api/events/${eventId}/build-checklist`),

  // Activities (Phase 2 step 1) - per-event scoped
  activities:        (eventId: number) =>
    api<Activity[]>(`/api/events/${eventId}/activities`),
  createActivity:    (eventId: number, body: ActivityCreate) =>
    api<Activity>(`/api/events/${eventId}/activities`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateActivity:    (eventId: number, id: number, body: ActivityUpdate) =>
    api<Activity>(`/api/events/${eventId}/activities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteActivity:    (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/activities/${id}`, { method: 'DELETE' }),

  // ---- Library Activities (mig 035, PR B) ----
  // Global catalog under /access/library-activities (admin-gated for
  // writes; reads open to any signed-in user via the picker).
  libraryActivities:        () =>
    api<LibraryActivity[]>('/api/access/library-activities'),
  createLibraryActivity:    (body: LibraryActivityCreate) =>
    api<LibraryActivity>('/api/access/library-activities', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchLibraryActivity:     (id: number, body: LibraryActivityPatch) =>
    api<LibraryActivity>(`/api/access/library-activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteLibraryActivity:    (id: number) =>
    api<void>(`/api/access/library-activities/${id}`, { method: 'DELETE' }),

  // Copy-from-library — bulk-pull selected library slugs onto an
  // event's Activities list. The inserted rows land in the same
  // dbo.Activities table as custom adds; `librarySlug` on the wire
  // distinguishes them. Library default_* values snapshot into the
  // live location / time / staff / supplier columns at copy time.
  copyActivitiesFromLibrary: (eventId: number, slugs: string[]) =>
    api<CopyActivitiesFromLibraryResult>(`/api/events/${eventId}/activities/copy-from-library`, {
      method: 'POST',
      body: JSON.stringify({ slugs })
    }),

  // Components (Phase 2 step 2) - per-event scoped. costEstimate field is
  // server-gated on hasFinancial; the wrapper just transports.
  components:        (eventId: number) =>
    api<Component[]>(`/api/events/${eventId}/components`),
  createComponent:   (eventId: number, body: ComponentCreate) =>
    api<Component>(`/api/events/${eventId}/components`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateComponent:   (eventId: number, id: number, body: ComponentUpdate) =>
    api<Component>(`/api/events/${eventId}/components/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteComponent:   (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/components/${id}`, { method: 'DELETE' }),

  // ---- Library Components (mig 034, PR A) ----
  // Global catalog under /access/library-components (admin-gated for
  // writes; reads open to any signed-in user via the picker).
  libraryComponents:        () =>
    api<LibraryComponent[]>('/api/access/library-components'),
  createLibraryComponent:   (body: LibraryComponentCreate) =>
    api<LibraryComponent>('/api/access/library-components', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchLibraryComponent:    (id: number, body: LibraryComponentPatch) =>
    api<LibraryComponent>(`/api/access/library-components/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteLibraryComponent:   (id: number) =>
    api<void>(`/api/access/library-components/${id}`, { method: 'DELETE' }),

  // Copy-from-library — bulk-pull selected library slugs onto an
  // event's Components list. The inserted rows land in the same
  // dbo.Components table as custom adds; `librarySlug` on the wire
  // distinguishes them. Per-event CRUD continues to use the existing
  // `eventComponents` / `createComponent` etc. above — no parallel
  // endpoint needed.
  copyComponentsFromLibrary: (eventId: number, slugs: string[]) =>
    api<CopyFromLibraryResult>(`/api/events/${eventId}/components/copy-from-library`, {
      method: 'POST',
      body: JSON.stringify({ slugs })
    }),

  // Services (Phase 2 step 3) - per-event scoped train timetable.
  services:          (eventId: number) =>
    api<Service[]>(`/api/events/${eventId}/services`),
  createService:     (eventId: number, body: ServiceCreate) =>
    api<Service>(`/api/events/${eventId}/services`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateService:     (eventId: number, id: number, body: ServiceUpdate) =>
    api<Service>(`/api/events/${eventId}/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteService:     (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/services/${id}`, { method: 'DELETE' }),

  // Dual-source timetable read for the Schedule tab. Returns manual
  // Services OR MERAC-projected rows depending on whether the event
  // has a MeracEventMap binding. Read-only — manual CRUD still goes
  // through /services above.
  eventTimetable:    (eventId: number) =>
    api<TimetablePayload>(`/api/events/${eventId}/timetable`),

  // Presentation timetable for the printed docs (Product Briefing §4 +
  // single-event Service Timetable PDF). Prefers manual Services, else the full
  // per-day MERAC projection.
  presentationTimetable: (eventId: number) =>
    api<PresentationTimetablePayload>(`/api/events/${eventId}/presentation-timetable`),

  // v2 Schedule (spec 15) ----------------------------------------------------
  importWtt:         (eventId: number, body: ImportWttBody) =>
    api<ImportWttResult>(`/api/events/${eventId}/services/import`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  serviceCrew:       (serviceId: number) =>
    api<ServiceCrew[]>(`/api/services/${serviceId}/crew`),
  assignServiceCrew: (serviceId: number, body: AssignCrewBody) =>
    api<ServiceCrew[]>(`/api/services/${serviceId}/crew`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  operatingDays:     (eventId: number) =>
    api<OperatingDay[]>(`/api/events/${eventId}/operating-days`),
  upsertOperatingDay:(eventId: number, date: string, body: UpsertOperatingDayBody) =>
    api<OperatingDay>(`/api/events/${eventId}/operating-days/${date}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  locos:             () => api<NameOption[]>('/api/lookup/locos'),
  stations:          () => api<NameOption[]>('/api/lookup/stations'),

  // Roster (rebuild — migs 039/040/042).
  //   - GET /api/events/{id}/roster returns roles (filtered to this
  //     event's EventRoles set) + days (event.start..end) + assignments
  //     (the array of filled cells).
  //   - POST /api/events/{id}/assignments creates one chip.
  //   - PATCH/DELETE on /access/roster-assignments/{id} (admin-only;
  //     /access/ prefix because SWA reserves /api/admin/*).
  roster:        (eventId: number) =>
    api<RosterResponse>(`/api/events/${eventId}/roster`),
  createRosterAssignment: (eventId: number, body: RosterAssignmentCreate) =>
    api<RosterAssignment>(`/api/events/${eventId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchRosterAssignment:  (id: number, body: RosterAssignmentPatch) =>
    api<RosterAssignment>(`/api/access/roster-assignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteRosterAssignment: (id: number) =>
    api<void>(`/api/access/roster-assignments/${id}`, { method: 'DELETE' }),

  // ---- Roles (global catalogue) ----
  // Admin-managed via /access/roles. Reads are open to any signed-in
  // user (the per-event Configure Roles picker pulls the full list).
  roles:         () => api<LibraryRole[]>('/api/access/roles'),
  createRole:    (body: LibraryRoleCreate) =>
    api<LibraryRole>('/api/access/roles', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchRole:     (id: number, body: LibraryRolePatch) =>
    api<LibraryRole>(`/api/access/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteRole:    (id: number) =>
    api<void>(`/api/access/roles/${id}`, { method: 'DELETE' }),
  // Replace the default "roles to call for" set (item 1). Returns the full
  // active catalogue with the new isDefault flags.
  setRoleDefaults: (roleIds: number[]) =>
    api<LibraryRole[]>('/api/access/roles/defaults', {
      method: 'POST',
      body: JSON.stringify({ roleIds })
    }),

  // ---- Event Roles (per-event toggle) ----
  // List returns ALL active roles with an `inUse` flag, so the
  // Configure Roles multi-picker renders the full catalogue with
  // ticked state. Set is replace-semantics: roleIds in body wins.
  eventRoles:    (eventId: number) =>
    api<EventRoleSummary[]>(`/api/events/${eventId}/roles`),
  setEventRoles: (eventId: number, body: EventRolesSetBody) =>
    api<EventRoleSummary[]>(`/api/events/${eventId}/roles/set`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  // ---- Events Live volunteer loop (mig 053) ----
  // Availability inbox + review, per-volunteer confirm-all, and the call
  // composer. All admin-gated server-side.
  volunteerOffers: (eventId: number) =>
    api<VolunteerOffer[]>(`/api/events/${eventId}/volunteer-offers`),
  reviewVolunteerOffer: (eventId: number, offerId: number, decision: 'approved' | 'declined') =>
    api<{ id: number; status: string }>(`/api/events/${eventId}/volunteer-offers/${offerId}/review`, {
      method: 'POST',
      body: JSON.stringify({ decision })
    }),
  confirmVolunteer: (eventId: number, userId: number) =>
    api<{ userId: number; confirmed: number }>(`/api/events/${eventId}/volunteers/${userId}/confirm`, {
      method: 'POST'
    }),
  // Group role approval (item 3) — confirm a whole role's roster at once.
  confirmRoleRoster: (eventId: number, roleId: number) =>
    api<{ roleId: number; confirmed: number; volunteers: number }>(`/api/events/${eventId}/roles/${roleId}/confirm`, {
      method: 'POST'
    }),
  volunteerCall: (eventId: number) =>
    api<VolunteerCall>(`/api/events/${eventId}/volunteer-call`),
  setVolunteerCall: (eventId: number, body: VolunteerCallSetBody) =>
    api<VolunteerCall>(`/api/events/${eventId}/volunteer-call`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  closeVolunteerCall: (eventId: number) =>
    api<VolunteerCall>(`/api/events/${eventId}/volunteer-call/close`, {
      method: 'POST'
    }),

  // ---- Roster Availability (per-event, mig 041) ----
  // GET /me returns the caller's rows for this event; GET (no /me)
  // returns every user's rows and is admin-gated server-side. POST is
  // an upsert batch — available:true MERGEs, available:false deletes.
  // Both reads share a cache key shape so the picker can read the
  // full-list result without a refetch when admins are around.
  myAvailability:        (eventId: number) =>
    api<RosterAvailability[]>(`/api/events/${eventId}/availability/me`),
  eventAvailability:     (eventId: number) =>
    api<RosterAvailability[]>(`/api/events/${eventId}/availability`),
  upsertAvailability:    (eventId: number, body: RosterAvailabilityUpsertBody) =>
    api<RosterAvailabilityUpsertResult>(`/api/events/${eventId}/availability`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  // Dashboard (Phase 2 step 6) - aggregated KPIs + upcoming + live banner.
  // Weather is a separate endpoint so it can be cached independently and
  // doesn't block the dashboard render.
  dashboard:    () => api<DashboardPayload>('/api/dashboard'),
  weather:      () => api<WeatherPayload>('/api/dashboard/weather'),

  // Checklists - template catalogue (admin) + per-event applied checklists.
  checklistTemplates:        () => api<ChecklistTemplateSummary[]>('/api/checklist-templates'),
  checklistTemplate:         (id: number) =>
    api<ChecklistTemplate>(`/api/checklist-templates/${id}`),
  createChecklistTemplate:   (body: ChecklistTemplateBody) =>
    api<ChecklistTemplate>('/api/checklist-templates', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateChecklistTemplate:   (id: number, body: ChecklistTemplateBody) =>
    api<ChecklistTemplate>(`/api/checklist-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteChecklistTemplate:   (id: number) =>
    api<void>(`/api/checklist-templates/${id}`, { method: 'DELETE' }),

  eventChecklist:            (eventId: number) =>
    api<EventChecklistResponse>(`/api/events/${eventId}/checklist`),
  applyChecklistTemplate:    (eventId: number, templateId: number) =>
    api<EventChecklistResponse>(`/api/events/${eventId}/checklist/apply`, {
      method: 'POST',
      body: JSON.stringify({ templateId })
    }),
  addChecklistItem:          (eventId: number, body: EventChecklistItemAdd) =>
    api<EventChecklistResponse>(`/api/events/${eventId}/checklist/items`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchChecklistItem:        (eventId: number, itemId: number, body: EventChecklistItemPatch) =>
    api<EventChecklistResponse>(`/api/events/${eventId}/checklist/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteChecklistItem:       (eventId: number, itemId: number) =>
    api<void>(`/api/events/${eventId}/checklist/items/${itemId}`, { method: 'DELETE' }),

  // Risks (spec 04)
  hazardLibrary:        () => api<HazardLibraryEntry[]>('/api/hazard-library'),
  createHazard:         (body: HazardLibraryCreate) =>
    api<HazardLibraryEntry>('/api/hazard-library', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchHazard:          (id: number, body: HazardLibraryPatch) =>
    api<HazardLibraryEntry>(`/api/hazard-library/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteHazard:         (id: number) =>
    api<void>(`/api/hazard-library/${id}`, { method: 'DELETE' }),
  eventRisks:           (eventId: number) =>
    api<EventRisk[]>(`/api/events/${eventId}/risks`),
  createEventRisk:      (eventId: number, body: EventRiskBody) =>
    api<EventRisk>(`/api/events/${eventId}/risks`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  createRisksFromLibrary: (eventId: number, libraryIds: number[]) =>
    api<EventRisk[]>(`/api/events/${eventId}/risks/from-library`, {
      method: 'POST',
      body: JSON.stringify({ libraryIds })
    }),
  updateEventRisk:      (eventId: number, riskId: number, body: EventRiskBody) =>
    api<EventRisk>(`/api/events/${eventId}/risks/${riskId}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  signOffEventRisk:     (eventId: number, riskId: number) =>
    api<EventRisk>(`/api/events/${eventId}/risks/${riskId}/sign-off`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  deleteEventRisk:      (eventId: number, riskId: number) =>
    api<void>(`/api/events/${eventId}/risks/${riskId}`, { method: 'DELETE' }),

  // Notes / decisions log (spec 09)
  notes:                (eventId: number) =>
    api<EventNote[]>(`/api/events/${eventId}/notes`),
  createNote:           (eventId: number, body: NoteCreateBody) =>
    api<EventNote>(`/api/events/${eventId}/notes`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchNote:            (eventId: number, noteId: number, body: NotePatchBody) =>
    api<EventNote>(`/api/events/${eventId}/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  pinNote:              (eventId: number, noteId: number, pinnedLabel: string | null) =>
    api<EventNote>(`/api/events/${eventId}/notes/${noteId}/pin`, {
      method: 'POST',
      body: JSON.stringify({ pinnedLabel })
    }),
  unpinNote:            (eventId: number, noteId: number) =>
    api<EventNote>(`/api/events/${eventId}/notes/${noteId}/unpin`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  deleteNote:           (eventId: number, noteId: number) =>
    api<void>(`/api/events/${eventId}/notes/${noteId}`, { method: 'DELETE' }),

  // Budget (spec 06)
  budgetCategories:     () => api<BudgetCategory[]>('/api/budget-categories'),
  eventBudget:          (eventId: number) =>
    api<BudgetPayload>(`/api/events/${eventId}/budget`),
  createBudgetLine:     (eventId: number, body: BudgetLineBody) =>
    api<unknown>(`/api/events/${eventId}/budget/lines`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateBudgetLine:     (eventId: number, lineId: number, body: BudgetLineBody) =>
    api<unknown>(`/api/events/${eventId}/budget/lines/${lineId}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteBudgetLine:     (eventId: number, lineId: number) =>
    api<void>(`/api/events/${eventId}/budget/lines/${lineId}`, { method: 'DELETE' }),
  baselineBudget:       (eventId: number) =>
    api<BudgetPayload>(`/api/events/${eventId}/budget/baseline`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  rebaselineBudget:     (eventId: number) =>
    api<BudgetPayload>(`/api/events/${eventId}/budget/rebaseline`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  eventMeracRollup:     (eventId: number) =>
    api<MeracRollupPayload>(`/api/events/${eventId}/merac-rollup`),
  // Batch MERAC summary for the Events list cards — one trip for the whole
  // grid (presence + ticket/income + last-refreshed, keyed by event_id).
  meracSummary:         () => api<MeracSummaryMap>('/api/events/merac-summary'),
  eventFinancials:      (eventId: number) =>
    api<FinancialsPayload>(`/api/events/${eventId}/financials`),
  eventIncome:          (eventId: number) =>
    api<EventIncomePayload>(`/api/events/${eventId}/income`),

  // Event actions (spec 30) — per-event to-do register
  eventActions:         (eventId: number) =>
    api<EventAction[]>(`/api/events/${eventId}/actions`),
  createAction:         (eventId: number, body: EventActionCreate) =>
    api<EventAction>(`/api/events/${eventId}/actions`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchAction:          (eventId: number, id: number, body: EventActionPatch) =>
    api<EventAction>(`/api/events/${eventId}/actions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteAction:         (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/actions/${id}`, { method: 'DELETE' }),

  // Project plan / Gantt (spec 32) — core CRUD. dependsOn edges
  // deferred (see api/src/handlers/planTasks.ts header).
  eventPlanTasks:       (eventId: number) =>
    api<PlanTask[]>(`/api/events/${eventId}/plan-tasks`),
  createPlanTask:       (eventId: number, body: PlanTaskCreate) =>
    api<PlanTask>(`/api/events/${eventId}/plan-tasks`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchPlanTask:        (eventId: number, id: number, body: PlanTaskPatch) =>
    api<PlanTask>(`/api/events/${eventId}/plan-tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deletePlanTask:       (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/plan-tasks/${id}`, { method: 'DELETE' }),

  // Station map markers (spec 31 / item 7) — per-event pins on a
  // station's base map. Optional ?station= filter scopes the list to
  // the active station.
  stationMarkers:       (eventId: number, station?: StationSlug) => {
    const q = station ? `?station=${encodeURIComponent(station)}` : ''
    return api<StationMarker[]>(`/api/events/${eventId}/station-markers${q}`)
  },
  createStationMarker:  (eventId: number, body: StationMarkerCreate) =>
    api<StationMarker>(`/api/events/${eventId}/station-markers`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  patchStationMarker:   (eventId: number, id: number, body: StationMarkerPatch) =>
    api<StationMarker>(`/api/events/${eventId}/station-markers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
  deleteStationMarker:  (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/station-markers/${id}`, { method: 'DELETE' }),

  // Briefings & comms (spec 05)
  briefingTopics:       () => api<BriefingTopic[]>('/api/briefing-topics'),
  // Guest FAQ library (mig 067) — create/edit/delete reuse the topic methods
  // above with isFaq set on the body.
  faqTopics:            () => api<BriefingTopic[]>('/api/faq-topics'),
  createBriefingTopic:  (body: BriefingTopicBody) =>
    api<BriefingTopic>('/api/briefing-topics', {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateBriefingTopic:  (id: number, body: BriefingTopicBody) =>
    api<BriefingTopic>(`/api/briefing-topics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteBriefingTopic:  (id: number) =>
    api<void>(`/api/briefing-topics/${id}`, { method: 'DELETE' }),

  eventBriefings:       (eventId: number) =>
    api<EventBriefingsPayload>(`/api/events/${eventId}/briefings`),
  addEventBriefings:    (eventId: number, topicIds: number[]) =>
    api<EventBriefingsPayload>(`/api/events/${eventId}/briefings`, {
      method: 'POST',
      body: JSON.stringify({ topicIds })
    }),
  removeEventBriefing:  (eventId: number, topicId: number) =>
    api<void>(`/api/events/${eventId}/briefings/${topicId}`, { method: 'DELETE' }),
  ackEventBriefing:     (eventId: number, topicId: number) =>
    api<EventBriefingsPayload>(`/api/events/${eventId}/briefings/${topicId}/ack`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  sendBriefingReminder: (eventId: number, topicId: number | null) =>
    api<CommsSendResult>(`/api/events/${eventId}/briefings/send-reminder`, {
      method: 'POST',
      body: JSON.stringify(topicId == null ? {} : { topicId })
    }),
  sendRosterConfirmation: (eventId: number) =>
    api<CommsSendResult>(`/api/events/${eventId}/roster/send-confirmation`, {
      method: 'POST',
      body: JSON.stringify({})
    }),

  // Reports (spec 07) - cross-event rollup.
  reportsRollup: (filters: ReportsFilters) => {
    const qs = new URLSearchParams()
    if (filters.fromDate) qs.set('from', filters.fromDate)
    if (filters.toDate) qs.set('to', filters.toDate)
    if (filters.types.length > 0) qs.set('types', filters.types.join(','))
    if (filters.status.length > 0) qs.set('status', filters.status.join(','))
    const q = qs.toString()
    return api<ReportsPayload>(`/api/reports/rollup${q ? '?' + q : ''}`)
  },

  // Volunteer (spec 11)
  volunteerShifts:  () => api<VolunteerShiftsPayload>('/api/volunteer/me/shifts'),
  volunteerProfile: () => api<VolunteerProfile>('/api/volunteer/me/profile'),
  updateVolunteerProfile: (body: VolunteerProfileBody) =>
    api<VolunteerProfile>('/api/volunteer/me/profile', {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  completeOnboarding: () =>
    api<{ ok: true }>('/api/volunteer/me/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify({})
    }),
  requestMagicLink: (email: string) =>
    api<{ ok: true; note?: string }>('/api/auth/request-magic-link', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  // Volunteer magic-link sign-in completion (Volunteer L2). The cookie set
  // by otp/consume is read server-side by withVolunteerAuth.
  volunteerMe: () =>
    api<{ signedIn: boolean; user?: { id: number; name: string | null } }>('/api/auth/volunteer-me'),
  submitOtp: (email: string, code: string) =>
    api<{ ok: true; user: { id: number; name: string | null } }>('/api/auth/otp', {
      method: 'POST',
      body: JSON.stringify({ email, code })
    }),
  consumeMagicLink: (token: string) =>
    api<{ ok: true; user: { id: number; name: string | null } }>('/api/auth/consume', {
      method: 'POST',
      body: JSON.stringify({ token })
    }),
  signOutVolunteer: () =>
    api<{ ok: true }>('/api/auth/sign-out', {
      method: 'POST',
      body: JSON.stringify({})
    }),

  // My Actions (spec 14)
  myActions: () => api<MyActionsPayload>('/api/me/actions'),

  // Admin users directory (spec 15)
  // Admin-only endpoints use `/api/access/*` not `/api/admin/*` —
  // Azure SWA reserves the `/api/admin` path prefix and silently 404s
  // user-registered handlers under it. SPA routes (/admin/users, etc.)
  // are unaffected; this is purely about the Function route shape.
  // See api/src/index.ts above adminUsersList for the full note.
  adminUsers: () => api<AdminUser[]>('/api/access/users'),
  adminUser:  (id: number) => api<AdminUserDetail>(`/api/access/users/${id}`),
  patchAdminUser: (id: number, body: AdminUserPatch) =>
    api<AdminUser>(`/api/access/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),

  // POST /api/access/users/from-entra (Tweak T5) — admin pre-create.
  // Returns 201 { user } on a fresh insert, 200 { error: 'already-exists',
  // user } if the row was already in dbo.Users. 4xx/5xx (not-found-in-entra,
  // invalid-email, graph-error) surface as thrown Errors via the wrapper.
  createUserFromEntra: (email: string) =>
    api<CreateUserFromEntraResult>('/api/access/users/from-entra', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  // MERAC event-code catalog for the event create/edit dropdown.
  // Admin-gated server-side. Defaults to active codes in group '19'
  // (event-rollup codes); other groups + all-codes mode reserved for
  // future surfaces. Path uses access/ for the same SWA-reservation
  // reason noted above.
  meracEventCodes: (opts?: { active?: 'true' | 'false' | 'all' }) =>
    api<MeracEventCode[]>(
      `/api/access/merac/event-codes${opts?.active ? `?active=${opts.active}` : ''}`
    ),

  // Per-event resources (Live View PR L1 / spec 23, mig 048). Categorised
  // list of links / shortcuts surfaced by ResourcesPanel.tsx. Reads open
  // to any signed-in user; attach/detach are POST bodies (not RESTful
  // /{id}) so the URL space stays flat.
  eventResources: (eventId: number) =>
    api<EventResource[]>(`/api/events/${eventId}/resources`),
  attachEventResource: (eventId: number, body: EventResourceAttach) =>
    api<EventResource>(`/api/events/${eventId}/resources/attach`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  detachEventResource: (eventId: number, resourceId: number) =>
    api<void>(`/api/events/${eventId}/resources/detach`, {
      method: 'POST',
      body: JSON.stringify({ resourceId })
    }),

  // Per-event Quick Reference (Resources Panel rebuild, spec 23 v2, mig 052).
  // Single-payload read for the rebuilt ResourcesPanel: per-event
  // narrative, activities, distinct on-duty contacts. Distinct from
  // eventResources above which serves spec 46's directory grid.
  quickReference: (eventId: number) =>
    api<QuickReferencePayload>(`/api/events/${eventId}/quick-reference`),

  // Live View (Live View PR L2 / spec 33 §7, migs 049+050).
  // Single-payload polling endpoint + sign-on/off toggles. The pulse
  // endpoint is polled every 30s via useLivePulse; the sign endpoints
  // are idempotent so the LiveTab toggle can fire-and-forget.
  livePulse: (eventId: number) =>
    api<LivePulsePayload>(`/api/events/${eventId}/live-pulse`),
  liveSignOn: (eventId: number) =>
    api<LiveShiftRecord>(`/api/events/${eventId}/shifts/sign-on`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  liveSignOff: (eventId: number) =>
    api<LiveShiftRecord>(`/api/events/${eventId}/shifts/sign-off`, {
      method: 'POST',
      body: JSON.stringify({})
    }),

  // Live flags (Live View PR L3 / spec 33 §6, mig 049). Six-category
  // alarm log. List default = open only; ?include=resolved for history.
  liveFlags: (eventId: number, opts?: { includeResolved?: boolean }) =>
    api<LiveFlagRecord[]>(
      `/api/events/${eventId}/live-flags${opts?.includeResolved ? '?include=resolved' : ''}`
    ),
  createLiveFlag: (eventId: number, body: LiveFlagCreateBody) =>
    api<LiveFlagRecord>(`/api/events/${eventId}/live-flags`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  resolveLiveFlag: (eventId: number, flagId: number) =>
    api<LiveFlagRecord>(`/api/events/${eventId}/live-flags/${flagId}`, {
      method: 'PATCH',
      body: JSON.stringify({})
    }),

  // Walkthrough toggle (Live View PR L3 / spec 33 §5.4). Dedicated PATCH
  // endpoint — see api/src/handlers/eventWalkthrough.ts for why it's
  // scoped rather than sharing the full event PUT.
  setEventWalkthrough: (eventId: number, completed: boolean) =>
    api<EventWalkthroughState>(`/api/events/${eventId}/walkthrough`, {
      method: 'PATCH',
      body: JSON.stringify({ completed })
    }),

  // Broadcasts (Live View PR L4, mig 051). Manager-only one-way log.
  // recipient_count is snapshotted server-side at send time.
  eventBroadcasts: (eventId: number) =>
    api<BroadcastRecord[]>(`/api/events/${eventId}/broadcasts`),
  createBroadcast: (eventId: number, body: BroadcastCreateBody) =>
    api<BroadcastRecord>(`/api/events/${eventId}/broadcasts`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  // Resources Directory (spec 46). Cross-event matrix. Top-level path
  // (NOT /api/admin/*) per the SWA-reserved-prefix rule. Any signed-in
  // user.
  resourcesDirectory: () =>
    api<ResourcesDirectoryPayload>('/api/resources-directory'),

  // Ticketing (Feature 4, migs 064/065) -------------------------------------
  // Standard catalogue. GET open to any signed-in user (dropdown); writes
  // admin-only server-side. Bare route (NOT /api/admin/*).
  ticketTypes: (opts?: { active?: 'true' | 'false' | 'all' }) =>
    api<TicketType[]>(`/api/ticket-types${opts?.active ? `?active=${opts.active}` : ''}`),
  createTicketType: (body: TicketTypeCreate) =>
    api<TicketType>('/api/ticket-types', { method: 'POST', body: JSON.stringify(body) }),
  patchTicketType: (id: number, body: TicketTypePatch) =>
    api<TicketType>(`/api/ticket-types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTicketType: (id: number) =>
    api<void>(`/api/ticket-types/${id}`, { method: 'DELETE' }),

  // Per-event ticket build.
  eventTicketTypes: (eventId: number) =>
    api<EventTicketType[]>(`/api/events/${eventId}/ticket-types`),
  createEventTicketType: (eventId: number, body: EventTicketTypeCreate) =>
    api<EventTicketType>(`/api/events/${eventId}/ticket-types`, { method: 'POST', body: JSON.stringify(body) }),
  patchEventTicketType: (eventId: number, id: number, body: EventTicketTypePatch) =>
    api<EventTicketType>(`/api/events/${eventId}/ticket-types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEventTicketType: (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/ticket-types/${id}`, { method: 'DELETE' }),

  // Phased MERAC matching.
  ticketMeracCandidates: (eventId: number) =>
    api<TicketMeracCandidate[]>(`/api/events/${eventId}/ticket-types/merac-candidates`),
  setTicketMeracMap: (eventId: number, id: number, mappings: TicketTypeMeracMapping[]) =>
    api<{ eventTicketTypeId: number; mappings: TicketTypeMeracMapping[] }>(
      `/api/events/${eventId}/ticket-types/${id}/merac-map`,
      { method: 'PUT', body: JSON.stringify({ mappings }) }
    ),

  // Resolved ticket pricing for the Product Briefing PDF §6 / FAQ.
  briefingTickets: (eventId: number) =>
    api<BriefingTicket[]>(`/api/events/${eventId}/briefing-tickets`),

  // Event Service Timetable header (Feature 2, mig 062).
  eventServiceInfo: (eventId: number) =>
    api<EventServiceInfo>(`/api/events/${eventId}/service-info`),
  putEventServiceInfo: (eventId: number, body: EventServiceInfo) =>
    api<EventServiceInfo>(`/api/events/${eventId}/service-info`, { method: 'PUT', body: JSON.stringify(body) }),

  // Catering (Feature 3, mig 063).
  eventCatering: (eventId: number) =>
    api<EventCatering[]>(`/api/events/${eventId}/catering`),
  createCatering: (eventId: number, body: EventCateringBody) =>
    api<EventCatering>(`/api/events/${eventId}/catering`, { method: 'POST', body: JSON.stringify(body) }),
  patchCatering: (eventId: number, id: number, body: EventCateringBody) =>
    api<EventCatering>(`/api/events/${eventId}/catering/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteCatering: (eventId: number, id: number) =>
    api<void>(`/api/events/${eventId}/catering/${id}`, { method: 'DELETE' }),
  reportsCatering: () =>
    api<CateringReportPayload>('/api/reports/catering'),
  reportsServiceTimetable: () =>
    api<ServiceTimetableReportPayload>('/api/reports/service-timetable'),

  // Product Briefing (Feature 1, mig 061).
  briefingDoc: (eventId: number) =>
    api<EventBriefingDoc>(`/api/events/${eventId}/briefing-doc`),
  putBriefingDoc: (eventId: number, body: EventBriefingDoc) =>
    api<EventBriefingDoc>(`/api/events/${eventId}/briefing-doc`, { method: 'PUT', body: JSON.stringify(body) }),
  faqSuggestion: (eventId: number) =>
    api<FaqSuggestion>(`/api/events/${eventId}/faq-suggestion`),

  // Claude Help — send the chat history, get the assistant's reply, which tools
  // it used, and any write proposals (pendingActions) awaiting approval.
  // Admin/manager only (server-gated).
  claudeHelp: (messages: Array<{ role: 'user' | 'assistant'; content: string }>) =>
    api<{ reply: string; toolsUsed: string[]; pendingActions?: ClaudePendingAction[] }>('/api/claude-help', {
      method: 'POST',
      body: JSON.stringify({ messages })
    }),
  // Apply user-approved write proposals.
  claudeHelpExecute: (actions: Array<{ type: string; payload: Record<string, unknown> }>) =>
    api<{ results: Array<{ type: string; ok: boolean; summary?: string; error?: string }> }>('/api/claude-help/execute', {
      method: 'POST',
      body: JSON.stringify({ actions })
    }),

  // Event flags (Phase 3 daily flagging).
  flags: () => api<{ flags: EventFlag[] }>('/api/flags'),
  resolveFlag: (id: number) => api<{ ok: boolean }>(`/api/flags/${id}/resolve`, { method: 'POST' }),

  // ---- Forecast Studio (migs 070-073) ----
  // Per-idea forecast. GET 404s when no forecast row exists yet — the client
  // then PUTs an initial row ("start forecast").
  forecast: (eventId: number) => api<ForecastPayload>(`/api/events/${eventId}/forecast`),
  putForecast: (eventId: number, body: Partial<ForecastPutBody>) =>
    api<ForecastPayload>(`/api/events/${eventId}/forecast`, { method: 'PUT', body: JSON.stringify(body) }),
  commitForecast: (eventId: number, opts?: { publishOnly?: boolean }) =>
    api<ForecastCommitResult>(`/api/events/${eventId}/forecast/commit${opts?.publishOnly ? '?publishOnly=1' : ''}`, { method: 'POST' }),
  forecastFinance: (eventId: number) =>
    api<ForecastFinancePayload>(`/api/events/${eventId}/forecast/finance`),
  addForecastMember: (eventId: number, body: { personId: number; functionId: number; commitmentText?: string | null }) =>
    api<{ team: ForecastTeamRow[]; phases: ForecastPhase[]; teamRowId?: number; notified?: string | null }>(`/api/events/${eventId}/forecast/team`, { method: 'POST', body: JSON.stringify(body) }),
  patchForecastMember: (rowId: number, body: { commitmentText?: string | null; approvalStatus?: 'pending' | 'approved' | 'blocked' }) =>
    api<{ team: ForecastTeamRow[] }>(`/api/forecast/team/${rowId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeForecastMember: (rowId: number) =>
    api<void>(`/api/forecast/team/${rowId}`, { method: 'DELETE' }),
  resendFunctionLink: (rowId: number) =>
    api<{ ok: boolean; emailedTo?: string; reason?: string }>(`/api/forecast/team/${rowId}/notify`, { method: 'POST' }),
  putForecastPhases: (eventId: number, phases: Array<Omit<ForecastPhase, 'id'>>) =>
    api<{ phases: ForecastPhase[] }>(`/api/events/${eventId}/forecast/phases`, { method: 'PUT', body: JSON.stringify({ phases }) }),

  // Running-timetable for a date range (proxied from The Big Plan) — for the
  // Studio's month calendar context.
  forecastTimetable: (from: string, to: string, calendar?: string) =>
    api<{ from: string; to: string; calendar: string | null; days: Record<string, { timetableCode: string; key: string | null; tone: string | null; colour: string | null; run: boolean }> }>(`/api/forecast/timetable?from=${from}&to=${to}${calendar ? `&calendar=${encodeURIComponent(calendar)}` : ''}`),
  forecastTimetableCalendars: () =>
    api<{ timetables: Array<{ id: string; name: string; version: string; status: string; effectiveFrom: string; effectiveTo: string }> }>(`/api/forecast/timetable-calendars`),

  // Business-plan versions (Claude-generated + human uploads).
  forecastPlanDocs: (eventId: number) =>
    api<{ docs: import('./types').ForecastPlanDoc[] }>(`/api/events/${eventId}/forecast/plan-docs`),
  uploadPlanDoc: (eventId: number, filename: string, dataBase64: string) =>
    api<{ docs: import('./types').ForecastPlanDoc[]; newId: number }>(`/api/events/${eventId}/forecast/plan-docs/upload`, { method: 'POST', body: JSON.stringify({ filename, dataBase64 }) }),

  // Contracts register (Sprint 1.5 #5).
  forecastContracts: (eventId: number) =>
    api<{ contracts: import('./types').ForecastContract[] }>(`/api/events/${eventId}/forecast/contracts`),
  createForecastContract: (eventId: number, body: import('./types').ForecastContractBody) =>
    api<{ contract: import('./types').ForecastContract }>(`/api/events/${eventId}/forecast/contracts`, { method: 'POST', body: JSON.stringify(body) }),
  updateForecastContract: (cid: number, body: import('./types').ForecastContractBody) =>
    api<{ contract: import('./types').ForecastContract }>(`/api/forecast/contracts/${cid}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteForecastContract: (cid: number) =>
    api<{ ok: boolean }>(`/api/forecast/contracts/${cid}`, { method: 'DELETE' }),
  uploadContractDoc: (cid: number, filename: string, dataBase64: string) =>
    api<{ contract: import('./types').ForecastContract }>(`/api/forecast/contracts/${cid}/document`, { method: 'POST', body: JSON.stringify({ filename, dataBase64 }) }),
  analyseContract: (cid: number) =>
    api<{ contract: import('./types').ForecastContract }>(`/api/forecast/contracts/${cid}/analyse`, { method: 'POST' }),

  // Change notice (mig 077) — formal sign-off + role-tagged feed on a forecast.
  changeNotice: (eventId: number) =>
    api<{ notice: import('./types').ChangeNotice | null }>(`/api/events/${eventId}/forecast/change-notice`),
  putChangeNotice: (eventId: number, body: import('./types').ChangeNoticeBody) =>
    api<{ notice: import('./types').ChangeNotice }>(`/api/events/${eventId}/forecast/change-notice`, { method: 'PUT', body: JSON.stringify(body) }),
  addCnSignoff: (eventId: number, body: { role: string; name?: string | null; personId?: number | null }) =>
    api<{ notice: import('./types').ChangeNotice }>(`/api/events/${eventId}/forecast/change-notice/signoff`, { method: 'POST', body: JSON.stringify(body) }),
  patchCnSignoff: (rowId: number, body: { status: import('./types').CnSignStatus }) =>
    api<{ notice: import('./types').ChangeNotice }>(`/api/forecast/cn-signoff/${rowId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteCnSignoff: (rowId: number) =>
    api<void>(`/api/forecast/cn-signoff/${rowId}`, { method: 'DELETE' }),
  addCnFeed: (eventId: number, body: { kind: import('./types').CnFeedKind; authorRole: import('./types').CnAuthorRole; body: string }) =>
    api<{ notice: import('./types').ChangeNotice }>(`/api/events/${eventId}/forecast/change-notice/feed`, { method: 'POST', body: JSON.stringify(body) }),

  // Core NYMR Functions library (admin-managed catalogue).
  libraryFunctions: () => api<LibraryFunction[]>('/api/access/library-functions'),
  createLibraryFunction: (body: LibraryFunctionBody) =>
    api<LibraryFunction>('/api/access/library-functions', { method: 'POST', body: JSON.stringify(body) }),
  patchLibraryFunction: (id: number, body: Partial<LibraryFunctionBody>) =>
    api<LibraryFunction>(`/api/access/library-functions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteLibraryFunction: (id: number) =>
    api<void>(`/api/access/library-functions/${id}`, { method: 'DELETE' }),

  // Market research — live Claude + hosted web search (admin-gated).
  researchChat: (eventId: number, messages: Array<{ role: 'user' | 'assistant'; content: string }>, brief: { sector: string; comps: string; questions: string }) =>
    api<ResearchChatResponse>('/api/forecast/research', { method: 'POST', body: JSON.stringify({ eventId, mode: 'chat', messages, brief }) }),
  researchRun: (eventId: number, brief: { sector: string; comps: string; questions: string }) =>
    api<ResearchRunResponse>('/api/forecast/research', { method: 'POST', body: JSON.stringify({ eventId, mode: 'run', brief }) })
}

// Body accepted by PUT /forecast — any subset of these sections is applied.
export interface ForecastPutBody {
  attendanceFc: number | null
  ticketYieldPence: number | null
  scenario: { att: number; yieldPence: number; costAdj: number }
  revenueStreams: import('./types').ForecastRevenueLine[]
  costLines: import('./types').ForecastCostLine[]
  manualBaseline: import('./types').ForecastManualBaseline | null
  brief: Partial<{ sector: string; comps: string; questions: string; concept: string }>
  insights: import('./types').Insight[]
  researchSummary: string
  proposed: { dates: string[]; calendar?: string }
  baselineLinks: Array<{ sourceEventId: number; selected: boolean }>
}

export interface ClaudePendingAction {
  type: string
  eventId: number | null
  summary: string
  payload: Record<string, unknown>
}
